/**
 * KNK-1.7 / FR-45 — Field profiles: each person's field details in this account.
 * KNK-1.54 / FR-46 — and leaving: Deactivate, Reactivate, and the Knock quarantine.
 *
 * An admin sets start date, role, active dates, worker type and display name. Quiet hours,
 * working days and home address are the rep's own (`save_field_rep_profile` refuses an admin
 * those), so they are shown read-only, and the home address only as "on file": the list never
 * carries it. Rules and copy live in `lib/fieldProfiles.ts` and `lib/fieldDeactivation.ts`.
 *
 * ⚠️ THERE IS NO "ACTIVE IN THE FIELD" SWITCH, and one must not come back. It was here until
 * 2026-10-10 and `save_field_rep_profile` now refuses `active` outright (20261010a1 DECISION 1):
 * FR-46 gates deactivation on `lead.manage` at Everyone scope and requires a previewed bulk move
 * plus a 72-hour quarantine, and a switch could do none of that. Deactivate below is the door.
 *
 * ⚠️ The Deactivate button is shown to everyone who can open this card, which needs only
 * `field.settings.manage`. That is deliberate: the real gate is a permission pair this screen
 * cannot read, so it asks and shows the server's refusal rather than guessing and hiding a
 * button from someone who is in fact allowed.
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import {
  FIELD_PROFILES_COPY as COPY,
  adminDraftFrom,
  adminPayload,
  quietHoursLine,
  roleLabel,
  validateAdminDraft,
  workingDaysLine,
  type AdminDraft,
  type FieldProfile,
} from '@/lib/fieldProfiles';
import {
  FIELD_DEACTIVATION_COPY as DCOPY,
  fieldState,
  fieldStateLabel,
  heldKnocks,
  movesLines,
  nothingToMove,
  quarantineLine,
  targetRequired,
  type DeactivationPreview,
  type Quarantine,
} from '@/lib/fieldDeactivation';
import { fetchFieldProfiles, saveFieldProfile } from '@/services/fieldProfileService';
import {
  deactivateFieldRep,
  fetchDeactivationPreview,
  fetchQuarantine,
  reactivateFieldRep,
  resolveQuarantine,
} from '@/services/fieldDeactivationService';

const WORKER_LABEL = { w2: 'W-2', '1099': '1099' } as const;

const STATE_BADGE: Record<string, 'secondary' | 'destructive'> = {
  inactive: 'secondary',
  deactivated: 'destructive',
};

export function FieldProfilesCard({ reloadKey = 0 }: { reloadKey?: number }) {
  const [profiles, setProfiles] = useState<FieldProfile[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<FieldProfile | null>(null);
  const [leaving, setLeaving] = useState<FieldProfile | null>(null);
  const [settling, setSettling] = useState<FieldProfile | null>(null);

  const load = useCallback(async () => {
    try {
      setProfiles(await fetchFieldProfiles());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, reloadKey]);

  return (
    <Card data-testid="knock-field-profiles">
      <CardHeader>
        <CardTitle>{COPY.profilesTitle}</CardTitle>
        <CardDescription>{COPY.profilesDescription}</CardDescription>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert variant="destructive">
            <AlertDescription>Could not read field profiles: {error}</AlertDescription>
          </Alert>
        )}
        {profiles === null && !error && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-label="Loading" />}
        {profiles !== null && (
          <ul className="divide-y divide-border rounded-md border border-border">
            {profiles.map((p) => {
              const state = fieldState(p);
              return (
              <li key={p.userId} className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between" data-testid={`field-profile-${p.userId}`}>
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate font-medium text-foreground">{p.displayName ?? p.name}</span>
                    {/* ⚠️ Three states, not two. "Not active in the field" is a date on their
                        profile; "Deactivated" means work moved and Knocks may be held. */}
                    {state !== 'active' && <Badge variant={STATE_BADGE[state]}>{fieldStateLabel(p)}</Badge>}
                    {p.deactivation && p.deactivation.held > 0 && (
                      <Badge variant="outline" data-testid={`field-held-${p.userId}`}>
                        {p.deactivation.held} Knock{p.deactivation.held === 1 ? '' : 's'} waiting
                      </Badge>
                    )}
                    {p.workerType && <Badge variant="outline">{WORKER_LABEL[p.workerType]}</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {[p.office ? `${p.office.name} Office` : 'No Office',
                      p.teams.length ? p.teams.map((t) => t.name).join(', ') : 'No team',
                      roleLabel(p)].join(' · ')}
                  </p>
                </div>
                <div className="flex shrink-0 flex-wrap gap-2 self-start sm:self-center">
                  {p.deactivation && (p.deactivation.held > 0 || p.deactivation.windowOpen) && (
                    <Button size="sm" variant="outline" onClick={() => setSettling(p)}
                      data-testid={`field-quarantine-${p.userId}`}>
                      {DCOPY.quarantineTitle}
                    </Button>
                  )}
                  <Button size="sm" variant={p.deactivation ? 'outline' : 'ghost'} onClick={() => setLeaving(p)}
                    data-testid={`field-leave-${p.userId}`}>
                    {p.deactivation ? DCOPY.reactivate : DCOPY.deactivate}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing(p)}>
                    {COPY.edit}
                  </Button>
                </div>
              </li>
              );
            })}
          </ul>
        )}
      </CardContent>
      {editing && (
        <EditProfileDialog
          profile={editing}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setProfiles((list) => list?.map((x) => (x.userId === saved.userId ? saved : x)) ?? null);
            setEditing(null);
          }}
        />
      )}
      {leaving && (
        <LeaveDialog
          profile={leaving}
          onClose={() => setLeaving(null)}
          onDone={() => {
            setLeaving(null);
            void load();
          }}
        />
      )}
      {settling && settling.deactivation && (
        <QuarantineDialog
          profile={settling}
          deactivationId={settling.deactivation.deactivationId}
          onClose={() => setSettling(null)}
          onDone={() => {
            setSettling(null);
            void load();
          }}
        />
      )}
    </Card>
  );
}

function EditProfileDialog({
  profile,
  onClose,
  onSaved,
}: {
  profile: FieldProfile;
  onClose: () => void;
  onSaved: (p: FieldProfile) => void;
}) {
  const { toast } = useToast();
  const [draft, setDraft] = useState<AdminDraft>(() => adminDraftFrom(profile));
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof AdminDraft>(k: K, v: AdminDraft[K]) => setDraft((d) => ({ ...d, [k]: v }));

  const problem = validateAdminDraft(draft);
  const payload = adminPayload(draft, profile);
  const dirty = Object.keys(payload).length > 0;

  const save = async () => {
    setBusy(true);
    try {
      onSaved(await saveFieldProfile(profile.userId, payload));
      toast({ title: 'Saved', description: `${profile.displayName ?? profile.name}'s field profile.` });
    } catch (e) {
      toast({ title: 'Not saved', description: e instanceof Error ? e.message : String(e), variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{profile.name}</DialogTitle>
          <DialogDescription>{profile.email}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="fp-display">Display name</Label>
            <Input id="fp-display" value={draft.displayName} maxLength={61} placeholder={profile.name}
              onChange={(e) => set('displayName', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="fp-start">Field start date</Label>
            <Input id="fp-start" type="date" value={draft.fieldStartDate} onChange={(e) => set('fieldStartDate', e.target.value)} />
            <p className="text-xs text-muted-foreground">
              {COPY.derivedStart}{!profile.fieldStartDateSet && profile.fieldStartDate ? ` Now ${profile.fieldStartDate}.` : ''}
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="fp-role">Primary field role</Label>
            <Select value={draft.fieldRole} onValueChange={(v) => set('fieldRole', v as AdminDraft['fieldRole'])}>
              <SelectTrigger id="fp-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="derived">From their work{!profile.fieldRoleSet && profile.fieldRole ? ` (${profile.fieldRole})` : ''}</SelectItem>
                <SelectItem value="setter">Setter</SelectItem>
                <SelectItem value="closer">Closer</SelectItem>
                <SelectItem value="both">Setter and closer</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="fp-worker">Worker type</Label>
            <Select value={draft.workerType} onValueChange={(v) => set('workerType', v as AdminDraft['workerType'])}>
              <SelectTrigger id="fp-worker"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unset">Not set</SelectItem>
                <SelectItem value="w2">W-2</SelectItem>
                <SelectItem value="1099">1099</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="fp-from">Active from</Label>
            <Input id="fp-from" type="date" value={draft.activeFrom} onChange={(e) => set('activeFrom', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="fp-until">Active until</Label>
            <Input id="fp-until" type="date" value={draft.activeUntil} onChange={(e) => set('activeUntil', e.target.value)} />
          </div>
        </div>

        <dl className="grid gap-x-4 gap-y-2 rounded-md bg-muted/50 p-3 text-sm sm:grid-cols-[auto_1fr]">
          <dt className="text-muted-foreground">Office</dt>
          <dd>{profile.office ? `${profile.office.name} (${profile.office.timezone ?? 'no timezone'})` : 'None'}</dd>
          <dt className="text-muted-foreground">Quiet hours</dt>
          <dd>{quietHoursLine(profile)}</dd>
          <dt className="text-muted-foreground">Working days</dt>
          <dd>{workingDaysLine(profile.workingDays)}</dd>
          <dt className="text-muted-foreground">Home address</dt>
          <dd>{profile.hasHomeAddress ? 'On file' : 'None'}</dd>
          <dt className="text-muted-foreground">Teams</dt>
          <dd>
            {profile.teamHistory.length === 0 ? 'None' : (
              <ul className="space-y-0.5">
                {profile.teamHistory.map((h, i) => (
                  <li key={i}>
                    {h.teamName}: {h.joinedAt.slice(0, 10)} to {h.leftAt ? h.leftAt.slice(0, 10) : 'now'}
                  </li>
                ))}
              </ul>
            )}
          </dd>
        </dl>

        {problem && (
          <Alert variant="destructive">
            <AlertDescription>{problem}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={() => void save()} disabled={busy || !dirty || problem !== null}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {COPY.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Deactivate, or reactivate. One dialog, because it is one decision from the manager's side and
 * "what happens to their work?" is the same question in both directions.
 *
 * The preview is fetched on open and is the ONLY source of what will move — the counts are the
 * server's, not derived here, so the sentence the manager reads and the rule the server applies
 * cannot drift.
 */
function LeaveDialog({
  profile,
  onClose,
  onDone,
}: {
  profile: FieldProfile;
  onClose: () => void;
  onDone: () => void;
}) {
  const { toast } = useToast();
  const deactivated = profile.deactivation !== null;
  const [preview, setPreview] = useState<DeactivationPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [target, setTarget] = useState<string>('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (deactivated) return;
    let live = true;
    void (async () => {
      try {
        const p = await fetchDeactivationPreview(profile.userId);
        if (live) {
          setPreview(p);
          setError(null);
        }
      } catch (e) {
        if (live) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      live = false;
    };
  }, [deactivated, profile.userId]);

  const held = profile.deactivation?.held ?? 0;
  const needsTarget = preview ? targetRequired(preview.moves) : false;
  const noCandidates = preview !== null && needsTarget && preview.candidates.length === 0;
  const ready = deactivated
    ? held === 0
    : preview !== null && !noCandidates && (!needsTarget || target !== '');

  const act = async () => {
    setBusy(true);
    try {
      if (deactivated) {
        await reactivateFieldRep(profile.userId);
        toast({ title: 'Reactivated', description: `${profile.displayName ?? profile.name} can log Knocks again.` });
      } else {
        await deactivateFieldRep(profile.userId, needsTarget ? target : null);
        toast({ title: 'Deactivated', description: `${profile.displayName ?? profile.name} is out of the field in this account.` });
      }
      onDone();
    } catch (e) {
      toast({ title: 'Not changed', description: e instanceof Error ? e.message : String(e), variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="field-leave-dialog">
        <DialogHeader>
          <DialogTitle>{deactivated ? DCOPY.reactivateTitle : DCOPY.deactivateTitle}</DialogTitle>
          <DialogDescription>
            {profile.displayName ?? profile.name} · {deactivated ? DCOPY.reactivateDescription : DCOPY.deactivateDescription}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {deactivated ? (
          <>
            {held > 0 && (
              <Alert variant="destructive">
                <AlertDescription>{DCOPY.reactivateBlocked}</AlertDescription>
              </Alert>
            )}
            <p className="text-sm text-muted-foreground">{DCOPY.knocksStay}</p>
          </>
        ) : (
          <>
            {preview === null && !error && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-label="Loading" />}
            {preview && (
              <>
                <ul className="list-disc space-y-1 pl-5 text-sm" data-testid="field-leave-moves">
                  {movesLines(preview.moves).map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
                {nothingToMove(preview.moves) && (
                  <p className="text-sm text-muted-foreground">{DCOPY.nothingToMove}</p>
                )}
                {noCandidates && (
                  <Alert variant="destructive">
                    <AlertDescription>{DCOPY.noCandidates}</AlertDescription>
                  </Alert>
                )}
                {needsTarget && preview.candidates.length > 0 && (
                  <div className="space-y-1.5">
                    <Label htmlFor="fd-target">{DCOPY.chooseTarget}</Label>
                    <Select value={target} onValueChange={setTarget}>
                      <SelectTrigger id="fd-target"><SelectValue placeholder="Choose a rep" /></SelectTrigger>
                      <SelectContent>
                        {preview.candidates.map((c) => (
                          <SelectItem key={c.userId} value={c.userId}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">{DCOPY.chooseTargetHint}</p>
                  </div>
                )}
              </>
            )}
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>{DCOPY.cancel}</Button>
          <Button variant={deactivated ? 'default' : 'destructive'} onClick={() => void act()} disabled={busy || !ready}
            data-testid="field-leave-confirm">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {deactivated ? DCOPY.reactivate : DCOPY.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The 72-hour quarantine: Knocks the rep's phone sent after they were deactivated, each waiting
 * for accept or discard.
 *
 * ⚠️ The window closing does NOT settle anything — `quarantineLine` is careful about that, and
 * the verdict buttons stay enabled after it closes, because the held Knocks are still held.
 */
function QuarantineDialog({
  profile,
  deactivationId,
  onClose,
  onDone,
}: {
  profile: FieldProfile;
  deactivationId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const { toast } = useToast();
  const [quarantine, setQuarantine] = useState<Quarantine | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    try {
      const q = await fetchQuarantine(deactivationId);
      setQuarantine(q);
      setChosen(new Set(heldKnocks(q).map((k) => k.knockId)));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [deactivationId]);

  useEffect(() => {
    void load();
  }, [load]);

  const held = quarantine ? heldKnocks(quarantine) : [];
  const toggle = (id: string) =>
    setChosen((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const settle = async (action: 'accept' | 'discard') => {
    const ids = held.filter((k) => chosen.has(k.knockId)).map((k) => k.knockId);
    if (ids.length === 0) return;
    setBusy(true);
    try {
      const { resolved, remaining } = await resolveQuarantine(deactivationId, ids, action);
      setDirty(true);
      toast({
        title: action === 'accept' ? 'Accepted' : 'Discarded',
        description: `${resolved} Knock${resolved === 1 ? '' : 's'} ${action === 'accept' ? 'accepted' : 'discarded'}. ${remaining} still waiting.`,
      });
      await load();
    } catch (e) {
      toast({ title: 'Not settled', description: e instanceof Error ? e.message : String(e), variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const close = () => (dirty ? onDone() : onClose());

  return (
    <Dialog open onOpenChange={(open) => !open && close()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="field-quarantine-dialog">
        <DialogHeader>
          <DialogTitle>{DCOPY.quarantineTitle}</DialogTitle>
          <DialogDescription>{profile.displayName ?? profile.name}</DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {quarantine === null && !error && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-label="Loading" />}

        {quarantine && (
          <>
            <p className="text-sm text-muted-foreground" data-testid="field-quarantine-line">{quarantineLine(quarantine)}</p>
            {held.length === 0 ? (
              <p className="text-sm">{DCOPY.noHeld}</p>
            ) : (
              <ul className="divide-y divide-border rounded-md border border-border">
                {held.map((k) => (
                  <li key={k.knockId} className="flex items-start gap-3 p-3">
                    <Checkbox id={`q-${k.knockId}`} checked={chosen.has(k.knockId)} onCheckedChange={() => toggle(k.knockId)} />
                    <div className="min-w-0 flex-1 space-y-0.5">
                      <Label htmlFor={`q-${k.knockId}`} className="block truncate font-medium">
                        {k.address ?? 'A Door with no address'}
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        {[k.disposition, k.localDay ?? k.deviceAt.slice(0, 10), k.late ? 'uploaded late' : null]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {held.length > 0 && (
              <div className="space-y-1 text-xs text-muted-foreground">
                <p>{DCOPY.acceptHint}</p>
                <p>{DCOPY.discardHint}</p>
              </div>
            )}
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={busy}>Close</Button>
          <Button variant="outline" onClick={() => void settle('discard')} disabled={busy || chosen.size === 0}
            data-testid="field-quarantine-discard">
            {DCOPY.discard}
          </Button>
          <Button onClick={() => void settle('accept')} disabled={busy || chosen.size === 0}
            data-testid="field-quarantine-accept">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {DCOPY.accept}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
